import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import {
    getPatients,
    getPatientById,
    getPatientCount,
    searchPatients,
    createPatient,
    updatePatient,
    deletePatient,
    removePatientFromPractice,
    updatePatientNotes,
} from "@/services/patient.service";
import type { Patient, PatientListItem, CreatePatientInput } from "@/types/patient";
import { useToast } from "@/components/shared/Toast";

// --- Query Keys ---
export const patientKeys = {
    all: ["patients"] as const,
    list: (practiceId?: string, options?: any) =>
        [...patientKeys.all, "list", practiceId, options].filter(Boolean),
    detail: (patientId: string, practiceId?: string) =>
        [...patientKeys.all, "detail", patientId, practiceId].filter(Boolean),
    count: (practiceId?: string, options?: any) =>
        [...patientKeys.all, "count", practiceId, options].filter(Boolean),
    search: (practiceId?: string, query?: string) =>
        [...patientKeys.all, "search", practiceId, query].filter(Boolean),
};

// --- Realtime Subscription Hook ---
export function usePatientsRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`patients-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "patient_practices",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: patientKeys.all });
                },
            )
            // Listen to patients table broadly. Note: no practice_id to filter by on this table.
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "patients",
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: patientKeys.all });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

// --- Queries ---
export function usePatientsQuery(practiceId?: string, options?: any) {
    return useQuery({
        queryKey: patientKeys.list(practiceId, options),
        queryFn: () => getPatients(practiceId!, options),
        enabled: !!practiceId,
    });
}

export function usePatientByIdQuery(patientId: string, practiceId?: string) {
    return useQuery({
        queryKey: patientKeys.detail(patientId, practiceId),
        queryFn: () => getPatientById(patientId, { practiceId }),
        enabled: !!patientId,
    });
}

export function usePatientCountQuery(practiceId?: string, options?: any) {
    return useQuery({
        queryKey: patientKeys.count(practiceId, options),
        queryFn: () => getPatientCount(practiceId!, options),
        enabled: !!practiceId,
    });
}

export function useSearchPatientsQuery(practiceId?: string, searchQuery?: string) {
    return useQuery({
        queryKey: patientKeys.search(practiceId, searchQuery),
        queryFn: () => searchPatients(practiceId!, searchQuery!),
        enabled: !!practiceId && !!searchQuery && searchQuery.length >= 2,
    });
}

// --- Mutations with Optimistic Updates ---
export function useCreatePatientMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (input: CreatePatientInput) => createPatient(practiceId!, input),
        onSuccess: () => {
            addToast({ type: "success", title: "Patient angelegt" });
            if (practiceId) queryClient.invalidateQueries({ queryKey: patientKeys.all });
        },
        onError: () => {
            addToast({ type: "error", title: "Fehler beim Anlegen des Patienten" });
        },
    });
}

export function useUpdatePatientMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ patientId, updates }: { patientId: string; updates: any }) =>
            updatePatient(patientId, updates),
        onMutate: async ({ patientId, updates }) => {
            await queryClient.cancelQueries({
                queryKey: patientKeys.detail(patientId, practiceId),
            });
            const previousPatient = queryClient.getQueryData<Patient>(
                patientKeys.detail(patientId, practiceId),
            );

            if (previousPatient) {
                queryClient.setQueryData<Patient>(patientKeys.detail(patientId, practiceId), {
                    ...previousPatient,
                    ...updates,
                });
            }
            return { previousPatient };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousPatient) {
                queryClient.setQueryData(
                    patientKeys.detail(_variables.patientId, practiceId),
                    context.previousPatient,
                );
            }
            addToast({ type: "error", title: "Fehler beim Speichern" });
        },
        onSettled: (_data, _err, _variables) => {
            queryClient.invalidateQueries({
                queryKey: patientKeys.detail(_variables.patientId, practiceId),
            });
            queryClient.invalidateQueries({ queryKey: patientKeys.list(practiceId) });
            queryClient.invalidateQueries({ queryKey: patientKeys.search(practiceId) });
        },
    });
}

export function useDeletePatientMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (patientId: string) => deletePatient(patientId),
        onSuccess: () => {
            addToast({ type: "success", title: "Patient gelöscht" });
            if (practiceId) queryClient.invalidateQueries({ queryKey: patientKeys.all });
        },
        onError: () => {
            addToast({ type: "error", title: "Löschen fehlgeschlagen" });
        },
    });
}

export function useRemovePatientFromPracticeMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ patientId, reason }: { patientId: string; reason?: string }) =>
            removePatientFromPractice(practiceId!, patientId, reason),
        onMutate: async ({ patientId }) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: patientKeys.list(practiceId) });

            const previousLists = queryClient.getQueriesData<PatientListItem[]>({
                queryKey: patientKeys.list(practiceId),
            });

            queryClient.setQueriesData<PatientListItem[]>(
                { queryKey: patientKeys.list(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.filter((p) => p.id !== patientId);
                },
            );

            return { previousLists };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousLists) {
                context.previousLists.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Entfernen fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: patientKeys.all });
        },
    });
}

export function useUpdatePatientNotesMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ patientId, notes }: { patientId: string; notes: string }) =>
            updatePatientNotes(patientId, notes),
        onMutate: async ({ patientId, notes }) => {
            await queryClient.cancelQueries({
                queryKey: patientKeys.detail(patientId, practiceId),
            });
            const previousPatient = queryClient.getQueryData<Patient>(
                patientKeys.detail(patientId, practiceId),
            );

            if (previousPatient) {
                queryClient.setQueryData<Patient>(patientKeys.detail(patientId, practiceId), {
                    ...previousPatient,
                    notes,
                });
            }
            return { previousPatient };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousPatient) {
                queryClient.setQueryData(
                    patientKeys.detail(_variables.patientId, practiceId),
                    context.previousPatient,
                );
            }
            addToast({ type: "error", title: "Notiz konnte nicht gespeichert werden" });
        },
        onSettled: (_data, _err, _variables) => {
            queryClient.invalidateQueries({
                queryKey: patientKeys.detail(_variables.patientId, practiceId),
            });
        },
    });
}
